import { Decorative } from "@faire/design-tokens";
import { Button } from "@faire/web--source/slate/Button/Button";
import { Chevron } from "@faire/web--source/slate/icons/foundation/Chevron";
import { Row, Column } from "@faire/web--source/slate/Layout";
import { getSpacing } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { EmbeddedLink } from "@faire/web--source/ui/EmbeddedLink";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { trackSamplesRetailerSampleRequestButtonWebView } from "@faire/web-api--source/events/samples/view/retailerSampleRequestButtonWeb";
import React, { Suspense } from "react";
import styled from "styled-components";

import { LazySamplesRequestModal } from "./LazySamplesRequestModal";
import { SamplesRequestStoreProvider } from "./SamplesRequestStore";

export interface ISamplesBannerDetails {
  title: string;
  message: string;
  buttonText: string;
  ctaLink: string;
  ctaText: string;
}

interface IProps {
  brandToken: string;
  productToken?: string;
  samplesBannerDetails: ISamplesBannerDetails;
}

export const RequestSamplesCTA: React.FunctionComponent<IProps> = ({
  brandToken,
  productToken,
  samplesBannerDetails,
}) => {
  const viewport = useViewport();

  const [showSamplesModal, setShowSamplesModal] = React.useState(false);
  const { title, message, buttonText, ctaLink, ctaText } = samplesBannerDetails;

  const viewRef = useDoActionWhenVisible(() => {
    trackSamplesRetailerSampleRequestButtonWebView(
      productToken !== undefined ? "PDP_BANNER" : "BRAND_PAGE_BANNER",
      brandToken,
      productToken ?? ""
    );
  });

  return (
    <>
      {showSamplesModal ? (
        <Suspense fallback={null}>
          <SamplesRequestStoreProvider>
            <LazySamplesRequestModal
              brandToken={brandToken}
              productToken={productToken}
              source={productToken !== undefined ? "PDP" : "BRAND"}
              onClose={() => {
                setShowSamplesModal(false);
              }}
            />
          </SamplesRequestStoreProvider>
        </Suspense>
      ) : null}
      <Container
        ref={viewRef}
        data-test-id="samples-request-cta"
        onClick={() => {
          // this banner should be clickable only on mobile
          if (viewport.isMobile) {
            setShowSamplesModal(true);
          }
        }}
      >
        <Column justify="center">
          <Typography variant="paragraphSansMedium">{title}</Typography>
          <Typography variant="paragraphSansRegular">
            {message}{" "}
            <EmbeddedLink
              onClick={(e) => {
                e.stopPropagation();
              }}
              linkTo={ctaLink}
            >
              {ctaText}
            </EmbeddedLink>
          </Typography>
        </Column>
        {viewport.isMobile ? (
          <Chevron direction="right" />
        ) : (
          <NoWrapButton
            onClick={() => setShowSamplesModal(true)}
            data-test-id="request-samples-cta"
          >
            {buttonText}
          </NoWrapButton>
        )}
      </Container>
    </>
  );
};

const Container = styled(Row).attrs({
  justify: "space-between",
  align: "center",
  gap: "1x",
})`
  width: 100%;
  background: ${Decorative.tint.orange};
  padding: ${getSpacing("2x")};
`;

const NoWrapButton = styled(Button).attrs({
  variant: "primary",
})`
  white-space: nowrap;
`;
